import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Mapbox from './Mapbox';
import SnowTable from './SnowTable';
import Legend from './Legend';

import '~styles/modules/graphic.scss';

import createLocationList from '../../lib/createLocationList';
import mapInteractions from '../../utils/mapInteractions';
import journalizeNumber from '../../utils/journalizeNumber';
import formatUpdateTime from '../../utils/formatUpdateTime';

const SnowSelector = styled.select`
  font-family: Georgia;
  font-weight: bold;
  border: none;
  border-bottom: 1px solid #afafaf;
  font-size: 1.125rem;
  padding: 0.25rem 0.5rem 0.25rem 0rem;
  background-color: #fff;
  border-radius: 0;
  color: black;
`;

const SourceLine = styled.p`
  padding: 0 20px;
`;

const Graphic = ({ data, setUpdateDate }) => {
  const isFirstRender = useRef(true);
  const hasSelectedState = useRef(false);
  const hasSelectedLocation = useRef(false);
  const map = useRef(null);
  const mapContainer = useRef(null);

  const [noLocations, setNoLocations] = useState(true);
  const [fullTableView, toggleFullTableView] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedState, setSelectedState] = useState('MA');
  const [stateName, setStateName] = useState('Massaschusetts');
  const [masterSnowFall, setMasterSnowFall] = useState({});
  const [locations, setLocations] = useState([]);
  const [mapWidth, setMapWidth] = useState(0);
  const [modifiedSnowFall, setModifiedSnowFall] = useState({});
  const [mapView, setMapView] = useState('3d');
  const [mapLoaded, setMapLoaded] = useState(false);

  // ----------------------------------------------------------------------
  // CHANGING STATES
  // Handles changes to the state select element by setting new values in state
  // for the selectedState and the stateName. Also resets the selectedLocation
  // value to 'all' and triggers the hasSelectedState ref to true
  // ----------------------------------------------------------------------

  const updateSelectedState = useCallback((e) => {
    setSelectedState(e.target.value);
    setStateName(e.target.options[e.target.selectedIndex].text);
    setSelectedLocation('all');
    hasSelectedState.current = true;
    hasSelectedLocation.current = false;
    toggleFullTableView(false);
  }, []);

  // ----------------------------------------------------------------------
  // CHANGING LOCATIONS
  // Handles changes to the location select element by setting a new value
  // in state for the selectedLocation to the selected value in the select elem.
  // ----------------------------------------------------------------------

  const updateSelectedLocation = useCallback((e) => {
    setSelectedLocation(e.target.value);
    hasSelectedLocation.current = true;
    toggleFullTableView(false);
  }, []);

  // ----------------------------------------------------------------------
  // SETTING THE LIST OF LOCATIONS
  // Handles creating a list of locations based on the selected state, along
  // with setting the value for the noLocations ref. These functions rely on
  // data being present, so it doesn't run on the first render.
  // ----------------------------------------------------------------------

  useEffect(() => {
    if (!isFirstRender.current) {
      setLocations(createLocationList(masterSnowFall, selectedState));

      if (locations.length === 0) {
        setNoLocations(true);
      } else {
        setNoLocations(false);
      }
    }

    if (masterSnowFall.data !== undefined) {
      setUpdateDate(masterSnowFall.update_date);
      const modifiedData = {
        data: {
          type: 'FeatureCollection',
          features: [],
        },
        update_date: masterSnowFall.update_date,
      };

      masterSnowFall.data.features.forEach((feature) => {
        const modifiedFeature = {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [feature.properties.long, feature.properties.lat],
          },
          properties: feature.properties,
        };

        modifiedData.data.features.push(modifiedFeature);
      });

      setModifiedSnowFall(modifiedData);
    }
  }, [selectedState, locations.length, masterSnowFall]);

  // ----------------------------------------------------------------------
  // SETTING THE FIRST RENDER VALUE
  // Changes the value of the isFirstRender ref after the first render occurs.
  // ----------------------------------------------------------------------

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  // ----------------------------------------------------------------------
  // HANDLING RESIZES AND SELECTIONS
  // Sets an event listener to reset the value of mapWidth on window resize.
  // This value is used to help determine the amount of padding when flying
  // the map to a fitted bounds within a given state (see mapInteractions.js)
  // Also handles animating the map when states are locations are selected
  // ----------------------------------------------------------------------

  useEffect(() => {
    function resetMapWidth() {
      setMapWidth(mapContainer.current.offsetWidth);
    }

    if (mapContainer.current) {
      setMapWidth(mapContainer.current.offsetWidth);
      window.addEventListener('resize', resetMapWidth);
    }

    if (hasSelectedState.current) {
      mapInteractions.flyToState(
        map.current,
        selectedState,
        selectedLocation,
        masterSnowFall.data,
        modifiedSnowFall.data,
        mapWidth,
      );
    }

    if (hasSelectedLocation.current) {
      mapInteractions.flyToLocationFromSelect(
        map.current,
        masterSnowFall.data,
        modifiedSnowFall.data,
        selectedLocation,
        selectedState,
        mapWidth,
      );
    }

    // eslint-disable-next-line
    return _ => {
      window.removeEventListener('resize', resetMapWidth);
    };
  }, [masterSnowFall.data, modifiedSnowFall.data, mapWidth, selectedState, selectedLocation]);

  return (
    <React.Fragment>
      <p className="paragraph content">Snow total data was last gathered from the National Weather Service at <strong>{formatUpdateTime(masterSnowFall.update_date)}</strong>.</p>
      <h3 className="section-header millerbanner-n4 content">Interested in a specific location?</h3>
      {masterSnowFall.data !== undefined
        && (
          <p className="content paragraph">There are currently <strong>{journalizeNumber(masterSnowFall.data.features.length)}</strong> locations across New England with reported snowfall over the last 48 hours. See how much snow &nbsp;
            <SnowSelector
              className="snow_selector"
              onChange={(e) => updateSelectedLocation(e)}
            >
              <option defaultValue disabled value="sample">Filter by location</option>
              <option value="all">all locations</option>
              {locations.map((location) => <option key={`${selectedState}-${location}`} value={location}>{location}</option>)}
            </SnowSelector>
            ,&nbsp;&nbsp;
            <SnowSelector
              className="snow__selector"
              onChange={(e) => updateSelectedState(e)}
            >
              <option defaultValue value="MA">Massachusetts</option>
              <option value="RI">Rhode Island</option>
              <option value="CT">Connecticut</option>
              <option value="NH">New Hampshire</option>
              <option value="VT">Vermont</option>
              <option value="ME">Maine</option>
            </SnowSelector>
            &nbsp; received.
          </p>
        )}
      <Legend />
      <div className="graphic__full" id={data.graphic}>
        {data.graphic === 'snowfall-map' && (
          <Mapbox
            setMasterSnowFall={setMasterSnowFall}
            modifiedSnowFall={modifiedSnowFall}
            selectedState={selectedState}
            setLocations={setLocations}
            mapRef={map}
            mapContainer={mapContainer}
            mapView={mapView}
            setMapView={setMapView}
            mapLoaded={mapLoaded}
            setMapLoaded={setMapLoaded}
          />
        )}
        <div className="graphic__footer">
          <SourceLine className="graphic__source bentonsanscond-n4">Source: {data.source}. {data.notes ? `Notes: ${data.notes}.` : null}</SourceLine>
          {data.credit ? <p className="graphic__credit bentonsanscond-n4">{data.credit}</p> : null}
        </div>
      </div>
      <SnowTable
        noLocations={noLocations}
        data={masterSnowFall}
        selectedState={selectedState}
        selectedLocation={selectedLocation}
        map={map}
        mapRef={mapContainer}
        stateName={stateName}
        fullTableView={fullTableView}
        toggleFullTableView={toggleFullTableView}
      />
    </React.Fragment>
  );
};

export default Graphic;

Graphic.propTypes = {
  data: PropTypes.shape({
    graphic: PropTypes.string,
    hed: PropTypes.string,
    chatter: PropTypes.string,
    source: PropTypes.string,
    notes: PropTypes.string,
    credit: PropTypes.string,
  }).isRequired,
  setUpdateDate: PropTypes.func.isRequired,
};
